/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import aiService, { useAITables, useTableColumns } from "api/aiService";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AILanguage, AILanguages } from "utils/aiHelpers";
import { LoadingButton } from "@mui/lab";
import { AITable } from "models/api/response.types";
import { PROD_HOSTNAME, companySettings } from "company-config";
import { useTranslation } from "react-i18next";

const AiTableColumnDialog: React.FC<{
  table: AITable;
  open: boolean;
  columnId?: number;
  setOpen: (open: boolean) => void;
}> = ({ table, columnId, open, setOpen }) => {
  const { t } = useTranslation();
  const { updateAITableMutation } = useAITables(table.organization_id);
  const { columns, upsertCachedColumn } = useTableColumns(table.id);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [name, setName] = useState("");
  const [language, setLanguage] = useState<AILanguage>("English (US)");
  const isProd = window.location.hostname === PROD_HOSTNAME;
  const [aiModel, setAiModel] = useState<"gpt-4o-mini" | "gpt-4o">(
    companySettings.defaultAIModel
  );

  useEffect(() => {
    setQuery("");
    if (columns && columnId) {
      const column = columns.find((c) => c.id === columnId);
      if (column) {
        setName(column.name);
        setQuery(column.query);
        setAiModel(column?.meta?.model || "gpt-4o-mini");
        setLanguage(
          column?.meta.language
            ? (column.meta.language as AILanguage)
            : "English (US)"
        );
      }
    }
  }, [columns, columnId]);

  const onClose = () => {
    setOpen(false);
    setQuery("");
    setLanguage("English (US)");
  };

  const handleCreate = () => {
    setLoading(true);
    aiService
      .createTableColumn(table.id, {
        query,
        name,
        order_id: (columns?.length || 0) + 1,
        meta: {
          language,
          model: aiModel,
        },
      })
      .then((response) => {
        upsertCachedColumn(response.data);
        onClose();
        updateAITableMutation.mutate({
          tableId: table.id,
          body: {
            meta: {
              ...table.meta,
              last_edited: new Date().toISOString(),
            },
          },
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUpdate = () => {
    if (columnId) {
      setLoading(true);
      aiService
        .updateTableColumn(table.id, columnId, {
          query,
          name,
          meta: {
            model: aiModel,
            language,
          },
        })
        .then((response) => {
          upsertCachedColumn(response.data);
          setOpen(false);
          updateAITableMutation.mutate({
            tableId: table.id,
            body: {
              meta: {
                ...table.meta,
                last_edited: new Date().toISOString(),
              },
            },
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && query.length === 0) {
      e.preventDefault();
      if (columnId) {
        handleUpdate();
      } else {
        handleCreate();
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {columnId ? t("edit_question") : t("add_question")}
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Select
          value={language}
          size="small"
          variant="outlined"
          fullWidth
          onChange={(e) => setLanguage(e.target.value as AILanguage)}
        >
          {AILanguages.map((lang) => (
            <MenuItem key={lang} value={lang}>
              <Typography variant="body2">{lang}</Typography>
            </MenuItem>
          ))}
        </Select>
        <FormControl
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputLabel-root": {
              color: "#272727",
              position: "relative",
              fontSize: "14px",
              transform: "none",
              marginBottom: "8px",
              display: "flex",
              alignItems: "center",
              gap: 1,
            },
          }}
        >
          <InputLabel shrink htmlFor="column-title">
            {t("column_title")}
            <Tooltip
              enterDelay={500}
              placement="top"
              title={t("title_optional_notice")}
            >
              <InfoOutlinedIcon fontSize="small" color="secondary" />
            </Tooltip>
          </InputLabel>
          <TextField
            id="column-title"
            variant="outlined"
            color="primary"
            value={name}
            size="small"
            placeholder={t("type_column_title")}
            fullWidth
            onChange={(e) => setName(e.target.value)}
          />
        </FormControl>
        {!isProd && (
          <FormControl
            variant="standard"
            fullWidth
            sx={{
              "& .MuiInputLabel-root": {
                color: "#272727",
                position: "relative",
                fontSize: "14px",
                transform: "none",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: 1,
              },
            }}
          >
            <InputLabel shrink>{t("ai_model")}</InputLabel>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                width: "100%",
              }}
            >
              <Typography className="body2" color="textSecondary">
                GPT-4o-mini
              </Typography>
              <Switch
                color="primary"
                size="small"
                checked={aiModel === "gpt-4o"}
                onChange={() => {
                  setAiModel(aiModel === "gpt-4o" ? "gpt-4o-mini" : "gpt-4o");
                }}
              />
              <Typography className="body2" color="textSecondary">
                GPT-4
              </Typography>
            </Box>
          </FormControl>
        )}
        <FormControl
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputLabel-root": {
              color: "#272727",
              position: "relative",
              fontSize: "14px",
              transform: "none",
              marginBottom: "8px",
            },
          }}
        >
          <InputLabel shrink htmlFor="ai-question">
            {t("ai_question")}
          </InputLabel>
          <TextField
            id="ai-question"
            variant="outlined"
            color="primary"
            value={query}
            size="small"
            placeholder={t("type_your_question")}
            fullWidth
            multiline
            minRows={3}
            onKeyDown={handleKeyPress}
            onChange={(e) => setQuery(e.target.value)}
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant="contained"
          disabled={query.length === 0 || loading}
          size="medium"
          color="primary"
          loading={loading}
          onClick={() => {
            if (columnId) {
              handleUpdate();
            } else {
              handleCreate();
            }
          }}
        >
          {columnId ? t("edit") : t("add")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AiTableColumnDialog;
